import {Injectable} from '@angular/core';
import {ModeratAnfrage} from "../anfrage/moderat.anfrage";
import {ModeratBackendService} from "./backend.service";
import {Router} from "@angular/router";
import {PortalService} from "../../services/portal.service";
import {EmbedService} from "../../services/embed.service";
import {RequestService} from "../../services/request.service";
import {RequestAnfrage} from "../../model/request.anfrage";
import {Observable, of} from "rxjs";
import {catchError, map, switchMap} from "rxjs/operators";
import {getResumeUrl} from "./request.resume";

/**
 * global advisordata that can be injected by the backend
 * @var object
 */
declare let advisorObjectData;

/**
 * Service for saving the request data locally in the browser to have it on all steps.
 */
@Injectable({
  providedIn: 'root'
})
export class ModeratRequestService extends RequestService {

  /**
   * @inheritdoc
   */
  requestObjectName = 'moc-anfrage';

  /**
   * constructor.
   */
  constructor(public backendService: ModeratBackendService, private router: Router,
              protected portalService: PortalService, protected embedService: EmbedService) {
    super(backendService, portalService, embedService);

    try {
      // preset and redirect only if we are inside the moc
      if (typeof advisorObjectData === "object" && typeof localStorage === "object" && window.location.href.indexOf('moderat') > -1) {
        // save injected data
        localStorage.setItem('moc-anfrage', JSON.stringify(advisorObjectData));
        // if we preset the data, redirect to the result step or - if there is no result yet - to the last step
        this.router.navigateByUrl(getResumeUrl(advisorObjectData, this.portalService.currentPortalId));
      }
    } catch (e) {

    }

    this.loadDataFromLocalStorage();
  }

  /**
   * @inheritdoc
   */
  getNewAnfrage(): RequestAnfrage {
    return new ModeratAnfrage();
  }

  /**
   * @inheritdoc
   */
  getAnfrage(): ModeratAnfrage {
    return super.getAnfrage() as ModeratAnfrage;
  }

  /**
   * Ensures `verbrauchsanalyseInput.fernwaermenetz` is set for the current request - used when
   * the fernwaerme/ergebnis step itself is skipped (interesse !== 'fernwaerme') but the address
   * step is still shown (showStepAddress), so nothing else in the flow ever runs this check.
   * Mirrors the check FernwaermeErgebnisComponent runs on its own step. Sets the value onto the
   * (shared-reference) anfrage synchronously once resolved and persists it in the background.
   */
  ensureDistrictHeatingStatus(): Observable<string> {
    const anfrage = this.getAnfrage();
    const input = anfrage.verbrauchsanalyseInput;

    if (!input?.adresse) {
      return this.saveFernwaermenetz(anfrage, 'keine_adresse');
    }

    return this.backendService.getRequestId().pipe(
      switchMap(requestId => this.backendService.districtHeatingStatus(requestId, {
        adresse_postalcode: input.adresse_postalcode,
        adresse_strasse: input.adresse_strasse,
        adresse_hausnummer: input.adresse_hausnummer,
      })),
      map(({status}) => status),
      catchError(() => of('nein')),
      switchMap(fernwaermenetz => this.saveFernwaermenetz(anfrage, fernwaermenetz)),
    );
  }

  /**
   * Merges `fernwaermenetz` into verbrauchsanalyseInput and persists it (localStorage + backend).
   */
  private saveFernwaermenetz(anfrage: ModeratAnfrage, fernwaermenetz: string): Observable<string> {
    anfrage.verbrauchsanalyseInput = {...anfrage.verbrauchsanalyseInput, fernwaermenetz};
    this.setAnfrage(anfrage);
    this.saveDataToServer();
    return of(fernwaermenetz);
  }
}
